import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { toast } from 'vue-sonner';
import http from '@/services/http';
import type { FavoriteFilter, FavoriteRuleSnapshot } from '@/utils/favoriteFilters';

/**
 * API layer for favorite filters (SAU-260). Favorites are per-user rows in
 * the backend (`favorite_filters` table), scoped per entity/page — the same
 * entity key the persistent table state is scoped by.
 *
 * The optimistic cache choreography lives HERE, in the mutations themselves
 * (onMutate patches, onError rolls back, onSettled reconciles) — the same
 * shape as `useSetUserPageGrant` in usePagesApi.ts. The mutation variables
 * carry the entity, so a mid-flight page switch can never make a mutation
 * settle against the wrong cache key. Fingerprint matching and entity
 * resolution live in `useFavoriteFilters`.
 */

const RESOURCE_BASE = '/api/favorite_filter';

export const favoriteFiltersQueryKey = (entityId: string) => ['favoriteFilters', entityId] as const;

/** Placeholder ids for optimistic rows — negative so they can never collide with real DB ids. */
let nextOptimisticFavoriteId = -1;

/**
 * True for the placeholder id of an optimistic row whose create POST hasn't
 * resolved yet. Such a row must never be DELETEd (the id doesn't exist
 * server-side); the UI shows it as pending instead.
 */
export function isOptimisticFavoriteId(favoriteFilterId: number): boolean {
  return favoriteFilterId < 0;
}

export interface FavoriteFilterCreateInput {
  entity: string;
  label: string;
  rule: FavoriteRuleSnapshot;
  /** The exact `favoriteRuleSignature` string — the backend dedupes on it. */
  signature: string;
}

export interface FavoriteFilterDeleteInput {
  entity: string;
  favoriteFilterId: number;
}

interface ListEnvelope<T> {
  data: T[];
}

interface ItemEnvelope<T> {
  data: T;
}

export function useFavoriteFiltersQuery(entityId: MaybeRefOrGetter<string>) {
  return useQuery({
    queryKey: computed(() => favoriteFiltersQueryKey(toValue(entityId))),
    queryFn: async ({ queryKey: [, entity] }): Promise<FavoriteFilter[]> => {
      const response = await http.get<ListEnvelope<FavoriteFilter>>(RESOURCE_BASE, {
        params: { entity },
      });
      return response.data?.data ?? [];
    },
    // Favorites only change through the mutations below, and each mutation
    // invalidates its entity's key on settle — so the cached list can never
    // go stale on its own. Without this, every mounted RuleRow star and
    // group menu (one observer each) refires the same GET on mount.
    staleTime: Infinity,
    // The backend may lag the frontend deploy (FE/BE deploys aren't atomic) —
    // a 404 won't heal on retry, so fail fast into the menu's error state.
    retry: false,
  });
}

export function useCreateFavoriteFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: FavoriteFilterCreateInput): Promise<FavoriteFilter> => {
      const response = await http.post<ItemEnvelope<FavoriteFilter>>(RESOURCE_BASE, input);
      const created = response.data?.data;
      if (!created) {
        throw new Error('Favorite filter create response is missing its data envelope.');
      }
      return created;
    },
    /**
     * Optimistic add: the star flips solid on the very next render instead of
     * waiting for the POST round-trip. The cache is patched with functional
     * updaters throughout — `setQueryData(key, undefined)` is a no-op in
     * TanStack Query v5, so a snapshot-style rollback would strand the phantom
     * row when the query has never been populated (e.g. pre-deploy 404).
     */
    onMutate: async (input) => {
      const queryKey = favoriteFiltersQueryKey(input.entity);
      // Cancel any in-flight refetch so it can't clobber our patch.
      await queryClient.cancelQueries({ queryKey });
      const optimistic: FavoriteFilter = {
        id: nextOptimisticFavoriteId--,
        entity: input.entity,
        label: input.label,
        rule: input.rule,
        signature: input.signature,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<FavoriteFilter[]>(queryKey, (old) => [...(old ?? []), optimistic]);
      return { queryKey, optimisticId: optimistic.id };
    },
    onError: (_error, _input, context) => {
      // Surgical rollback: drop exactly the row this mutation added. Unlike a
      // whole-list snapshot restore this can't clobber a concurrent mutation's
      // optimistic patch, so no refcounted snapshot bookkeeping is needed
      // (cf. usePagesApi, where the patch isn't row-addressable).
      if (context) {
        queryClient.setQueryData<FavoriteFilter[]>(context.queryKey, (old) =>
          (old ?? []).filter((favorite) => favorite.id !== context.optimisticId),
        );
      }
      toast.error('Could not save the favorite.');
    },
    onSettled: (_data, _error, input) => {
      // Reconcile against the truth — keyed off the ENTITY THE MUTATION WAS
      // ISSUED FOR (the variables), never the entity active at resolve time.
      queryClient.invalidateQueries({ queryKey: favoriteFiltersQueryKey(input.entity) });
    },
  });
}

export function useDeleteFavoriteFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ favoriteFilterId }: FavoriteFilterDeleteInput) => {
      await http.delete(`${RESOURCE_BASE}/${favoriteFilterId}`);
      return favoriteFilterId;
    },
    /** Optimistic removal — see useCreateFavoriteFilter for the conventions. */
    onMutate: async (input) => {
      const queryKey = favoriteFiltersQueryKey(input.entity);
      await queryClient.cancelQueries({ queryKey });
      const removed = queryClient
        .getQueryData<FavoriteFilter[]>(queryKey)
        ?.find((favorite) => favorite.id === input.favoriteFilterId);
      queryClient.setQueryData<FavoriteFilter[]>(queryKey, (old) =>
        (old ?? []).filter((favorite) => favorite.id !== input.favoriteFilterId),
      );
      return { queryKey, removed };
    },
    onError: (_error, _input, context) => {
      // Re-insert the exact row we removed (unless a refetch already restored
      // it); onSettled's invalidation re-establishes the server ordering.
      const removed = context?.removed;
      if (context && removed) {
        queryClient.setQueryData<FavoriteFilter[]>(context.queryKey, (old) => {
          const list = old ?? [];
          return list.some((favorite) => favorite.id === removed.id) ? list : [...list, removed];
        });
      }
      toast.error('Could not remove the favorite.');
    },
    onSettled: (_data, _error, input) => {
      queryClient.invalidateQueries({ queryKey: favoriteFiltersQueryKey(input.entity) });
    },
  });
}
